import { useCallback, useState } from "react";

import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { Button } from "@shared/buttons/button";
import { Modal } from "@shared/modal";

export type ManagerUser = {
  id: string;
  email: string;
  isAdmin: boolean;
  status: "loading" | "verified" | "not-verified" | "error";
};

export const AccountManagersTableV2 = ({
  users,
  removeManager,
  canRemove,
  resendInvite,
  resendInviteLoading,
}: {
  users: ManagerUser[];
  removeManager: (id: string) => void;
  canRemove: (id: string) => boolean;
  resendInvite: (emailAddress: string) => void;
  resendInviteLoading: boolean;
}) => {
  const { user, actingUser } = useUser();
  const setActingUser = useCallback(
    async (id: string) => {
      actingUser.setActingUserId(id);

      window.location.reload();
    },
    [actingUser],
  );

  const renderManagerStatus = (user: ManagerUser) => {
    if (!user.status) {
      return null;
    }

    switch (user.status) {
      case "error":
        return <span className="text-danger">Error loading details</span>;
      case "loading":
        return (
          <span
            className="spinner-border spinner-border-sm"
            role="status"
            aria-hidden="true"
          />
        );
      case "verified":
        return (
          <div className="inline-flex gap-2 items-center justify-center">
            <i className="w-2 h-2 bg-alien rounded" />
            <span>Active</span>
          </div>
        );
      case "not-verified":
        return (
          <div className="inline-flex gap-2 items-center justify-center">
            <i className="w-2 h-2 bg-sun rounded" />
            <span>Invited</span>
          </div>
        );
    }
  };

  const [userToRemove, setUserToRemove] = useState<ManagerUser | null>(null);
  const [resendUserInvite, setResendUserInvite] = useState<ManagerUser | null>(
    null,
  );

  return (
    <>
      <Modal
        isOpen={!!userToRemove}
        titleContent={<div className="font-bold">Remove User</div>}
        hideCloseButton
        onClose={() => setUserToRemove(null)}
      >
        <p>
          Remove <span className="font-bold">{userToRemove?.email}</span> from
          Northbeam?
        </p>
        <div className="flex items-center justify-end gap-2">
          <Button
            className="uppercase text-cosmic hover:bg-cosmic-3"
            onClick={() => setUserToRemove(null)}
          >
            No, exit
          </Button>
          <Button
            variant="danger"
            className="uppercase"
            onClick={() => {
              if (userToRemove) {
                removeManager(userToRemove.id);
              }
              setUserToRemove(null);
            }}
          >
            Yes, remove
          </Button>
        </div>
      </Modal>
      <table
        className="table-fixed text-sm
          outline outline-1 outline-gray-1 rounded-lg border-collapse table-striped
          [&_tr]:h-10
          [&_td]:pl-4 [&_td]:pr-2 [&_th]:pl-4 [&_th]:pr-2
        "
      >
        <thead>
          <tr>
            <th className=" border-r font-semibold">Email</th>
            <th className=" border-r font-semibold">Status</th>
            <th className="w-[33%] font-semibold">Manage</th>
          </tr>
        </thead>
        <tbody>
          {users.map((managerUser) => {
            const isAdmin = managerUser.email === user.impersonatedUser?.email;
            return (
              <tr
                className="border-t  odd:bg-cosmic-5 even:bg-white"
                key={managerUser.email}
              >
                <td className="border-r font-mono font-normal text-sm">
                  <div className="inline-flex items-center">
                    {managerUser.email}
                    {isAdmin && (
                      <div className="ml-2 px-4 py-[6px] bg-cosmic-3 text-cosmic rounded  flex items-center justify-center  text-sm">
                        <GenericTooltip
                          noInfoCircle
                          content={
                            <div>
                              <div>
                                Only your admin is able to delete account
                                managers.
                              </div>
                              <div className="mt-1">
                                If you want to change your account owner admin
                                user, please log an inquiry to our support team{" "}
                                <a
                                  href="https://www.northbeam.io/submit-a-support-ticket"
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  here.
                                </a>
                              </div>
                            </div>
                          }
                        >
                          <span className="font-sans">Account Owner</span>
                        </GenericTooltip>
                      </div>
                    )}
                  </div>
                </td>
                <td className="border-r font-mono font-normal text-sm">
                  <div className="flex justify-between items-center">
                    {renderManagerStatus(managerUser)}
                    {managerUser.status === "not-verified" && (
                      <Button
                        className="w-[105px] px-2 py-[6px] uppercase text-xs font-semibold text-cosmic hover:bg-cosmic-4 font-sans"
                        onClick={() => {
                          setResendUserInvite(managerUser);
                          resendInvite(managerUser.email);
                        }}
                        loading={
                          resendUserInvite === managerUser &&
                          resendInviteLoading
                        }
                      >
                        Resend invite
                      </Button>
                    )}
                  </div>
                </td>
                <td>
                  {managerUser.status === "loading" && (
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    />
                  )}
                  {managerUser.status !== "loading" &&
                    canRemove(managerUser.id) && (
                      <Button
                        className="px-2 py-[6px] uppercase text-xs font-semibold text-mars hover:bg-mars-2"
                        onClick={() => setUserToRemove(managerUser)}
                      >
                        <i className="mr-2 fa-regular fa-sharp fa-trash" />
                        Remove from account
                      </Button>
                    )}
                  {managerUser.status !== "loading" && user.isAdmin && (
                    <Button
                      className="px-2 py-[6px] uppercase text-xs font-semibold text-cosmic hover:bg-cosmic-4"
                      onClick={() => setActingUser(managerUser.id)}
                      isDisabled={managerUser.isAdmin}
                    >
                      <i className="fa-regular fa-sharp" />
                      Impersonate
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
};
